// frontend/admin-portal/src/components/settings/sections/ExchangeRatesSettings.jsx
import React, { useState } from "react";
import SettingItem from "../items/SettingItem";
import TextInput from "../items/TextInput";
import NumberInput from "../items/NumberInput";
import ToggleSwitch from "../items/ToggleSwitch";
import SelectInput from "../items/SelectInput";
import { Button, Modal, Alert } from "@fiberhome/shared-ui";
import { settingsService } from "../../../services/settingsService";
import { useSettings } from "../../../contexts/SettingsContext";

export default function ExchangeRatesSettings({ values = {}, onChange }) {
  const safeOnChange = typeof onChange === "function" ? onChange : () => {};
  const { refresh } = useSettings();

  const anchor = values["finance.anchorCurrency"] || "IQD";
  const base = values["finance.baseCurrency"] || anchor;

  const enabled = Array.isArray(values["finance.enabledCurrencies"])
    ? values["finance.enabledCurrencies"]
    : ["IQD", "USD", "EUR"];

  const rates = React.useMemo(() => {
    const map = {};
    for (const code of enabled) {
      if (code === anchor) map[code] = 1;
      else {
        const v = values[`finance.exchangeRate.${code}`];
        map[code] = typeof v === "number" ? v : Number(v) || 0;
      }
    }
    return map;
  }, [values, enabled, anchor]);

  const computeRate = (target) => {
    if (target === base) return 1;
    if (base === anchor) {
      const r = rates[target];
      return r > 0 ? 1 / r : 0;
    }
    if (target === anchor) return rates[base] || 0;
    const rBase = rates[base];
    const rTarget = rates[target];
    return rTarget > 0 ? rBase / rTarget : 0;
  };

  const computeInverseRate = (target) => {
    const r = computeRate(target);
    return r > 0 ? 1 / r : 0;
  };

  const targets = enabled.filter((c) => c !== base);

  // ----- Anchor change flow -----
  const [anchorModalOpen, setAnchorModalOpen] = useState(false);
  const [pendingAnchor, setPendingAnchor] = useState(anchor);
  const [anchorBusy, setAnchorBusy] = useState(false);
  const [anchorError, setAnchorError] = useState("");

  const openAnchorModal = () => {
    setPendingAnchor(anchor);
    setAnchorError("");
    setAnchorModalOpen(true);
  };

  const confirmAnchorChange = async () => {
    if (pendingAnchor === anchor) {
      setAnchorModalOpen(false);
      return;
    }
    setAnchorBusy(true);
    setAnchorError("");
    try {
      await settingsService.changeAnchor(pendingAnchor);
      await refresh(); // reload settings in context
      setAnchorModalOpen(false);
    } catch (err) {
      setAnchorError(
        err.response?.data?.message || err.message || "Failed to change anchor",
      );
    } finally {
      setAnchorBusy(false);
    }
  };

  return (
    <div className="fh-settings-list">
      <SettingItem
        label="Anchor Currency"
        description="Fixed reference for all rates. Changing it recomputes every stored rate."
      >
        <div className="d-flex align-items-center gap-2">
          <span
            style={{
              padding: "0.55rem 0.75rem",
              border: "1px solid var(--fh-border)",
              borderRadius: "var(--fh-radius-sm)",
              background: "var(--fh-surface-alt)",
              fontWeight: 600,
              minWidth: 80,
              textAlign: "center",
            }}
          >
            {anchor}
          </span>
          <Button variant="outline" size="sm" onClick={openAnchorModal}>
            Change…
          </Button>
        </div>
      </SettingItem>

      <SettingItem
        label="Base Currency"
        description="Books and reports are shown in this currency"
      >
        <SelectInput
          value={base}
          onChange={(v) => safeOnChange("finance.baseCurrency", v)}
          options={enabled.map((c) => ({ value: c, label: c }))}
        />
      </SettingItem>

      <SettingItem
        label="Enabled Currencies"
        description="Comma-separated — e.g. IQD, USD, EUR"
      >
        <TextInput
          value={enabled.join(", ")}
          onChange={(v) =>
            safeOnChange(
              "finance.enabledCurrencies",
              v
                .split(",")
                .map((s) => s.trim().toUpperCase())
                .filter(Boolean),
            )
          }
        />
      </SettingItem>

      <hr />

      <div
        className="mb-2"
        style={{ fontSize: 12, color: "var(--fh-text-muted)" }}
      >
        Rates shown as <strong>1 {base} = X target</strong>
        &nbsp;·&nbsp; stored as <strong>1 target = Y {anchor}</strong>
      </div>

      {targets.length === 0 && (
        <div className="text-muted" style={{ fontSize: 13, padding: "1rem 0" }}>
          No other currencies enabled.
        </div>
      )}

      {targets.map((code) => (
        <SettingItem
          key={code}
          label={`1 ${base} = ? ${code}`}
          description={`Stored as: 1 ${code} = N ${anchor}`}
        >
          <div>
            <NumberInput
              value={Number(computeRate(code).toFixed(code === "IQD" ? 2 : 6))}
              onChange={(v) => {
                if (base === anchor) {
                  if (v > 0)
                    safeOnChange(`finance.exchangeRate.${code}`, 1 / v);
                } else if (code === anchor) {
                  safeOnChange(`finance.exchangeRate.${base}`, v);
                } else {
                  const rBase = rates[base] || 1;
                  if (v > 0)
                    safeOnChange(`finance.exchangeRate.${code}`, rBase / v);
                }
              }}
              min={0}
              step={0.000001}
            />
            <div
              style={{
                fontSize: 11,
                color: "var(--fh-text-muted)",
                marginTop: 4,
              }}
            >
              Inverse: 1 {code} = {computeInverseRate(code).toFixed(6)} {base}
            </div>
          </div>
        </SettingItem>
      ))}

      <hr />

      <SettingItem
        label="Auto-update Exchange Rates"
        description="Fetch from an online API (requires API key)"
      >
        <ToggleSwitch
          checked={values["finance.autoUpdateRates"]}
          onChange={(v) => safeOnChange("finance.autoUpdateRates", v)}
        />
      </SettingItem>

      {/* Anchor change confirmation modal */}
      <Modal
        open={anchorModalOpen}
        onClose={() => !anchorBusy && setAnchorModalOpen(false)}
        title="Change Anchor Currency?"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setAnchorModalOpen(false)}
              disabled={anchorBusy}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={confirmAnchorChange}
              loading={anchorBusy}
              disabled={pendingAnchor === anchor}
            >
              Change Anchor
            </Button>
          </>
        }
      >
        {anchorError && (
          <Alert variant="danger" className="mb-3">
            {anchorError}
          </Alert>
        )}

        <Alert variant="warning" title="This action rewrites stored rates">
          Every <code>finance.exchangeRate.*</code> value will be recalculated
          so that the currency relationships stay the same.
        </Alert>

        <div className="mt-3">
          <label className="fh-input-label">New Anchor Currency</label>
          <select
            className="fh-input"
            value={pendingAnchor}
            onChange={(e) => setPendingAnchor(e.target.value)}
            disabled={anchorBusy}
          >
            {enabled.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div
          className="mt-2"
          style={{ fontSize: 12, color: "var(--fh-text-muted)" }}
        >
          Current anchor: <strong>{anchor}</strong> → New anchor:{" "}
          <strong>{pendingAnchor}</strong>
        </div>
      </Modal>
    </div>
  );
}
